/* =========================================================================
   WEAR CLOVERSE — Premium interaction layer
   Scroll progress, back-to-top, add-to-cart toasts.
   Singletons mounted once on the document; house utilities reused.
   ========================================================================= */

import { prefersReducedMotion } from '@theme/utilities';
import { ThemeEvents, CartAddEvent } from '@theme/events';

const ns = 'http://www.w3.org/2000/svg';

function svgIcon(paths, viewBox = '0 0 24 24') {
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('viewBox', viewBox);
  svg.setAttribute('aria-hidden', 'true');
  svg.innerHTML = paths;
  return svg;
}

/* ---------------------------------------------------------------- Scroll progress */
function initScrollProgress() {
  const bar = document.createElement('div');
  bar.className = 'cloverse-scroll-progress';
  document.body.appendChild(bar);

  let ticking = false;
  const update = () => {
    const doc = document.documentElement;
    const max = doc.scrollHeight - doc.clientHeight;
    const ratio = max > 0 ? doc.scrollTop / max : 0;
    bar.style.transform = `scaleX(${ratio})`;
    ticking = false;
  };

  window.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    },
    { passive: true }
  );
  update();
}

/* ---------------------------------------------------------------- Back to top */
function initBackToTop() {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'cloverse-back-to-top';
  button.setAttribute('aria-label', 'Back to top');
  button.appendChild(svgIcon('<path d="M12 19V5M5 12l7-7 7 7"/>'));
  document.body.appendChild(button);

  button.addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  });

  let ticking = false;
  const update = () => {
    if (window.scrollY > window.innerHeight * 0.6) {
      button.setAttribute('data-visible', '');
    } else {
      button.removeAttribute('data-visible');
    }
    ticking = false;
  };

  window.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    },
    { passive: true }
  );
  update();
}

/* ---------------------------------------------------------------- Toasts */
function initToasts() {
  const root = document.createElement('div');
  root.className = 'cloverse-toast-root';
  root.setAttribute('aria-live', 'polite');
  root.setAttribute('role', 'status');
  document.body.appendChild(root);

  const showToast = (message) => {
    const toast = document.createElement('div');
    toast.className = 'cloverse-toast';
    toast.appendChild(svgIcon('<path d="M20 6 9 17l-5-5"/>'));
    const span = document.createElement('span');
    span.textContent = message;
    toast.appendChild(span);
    root.appendChild(toast);

    requestAnimationFrame(() => toast.setAttribute('data-visible', ''));

    setTimeout(() => {
      toast.removeAttribute('data-visible');
      toast.addEventListener('transitionend', () => toast.remove(), { once: true });
    }, 2600);
  };

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    if (!(event instanceof CartAddEvent)) return;
    if (event.detail?.data?.didError) return;
    showToast('Added to cart');
  });
}

/* ------------------------------------------- Sticky add-to-cart clearance */
/* On mobile the sticky add-to-cart bar is full width and flush to bottom: 0,
   which is exactly where the back-to-top button sits. The bar's height is
   content-driven, so publish it as a custom property and let CSS lift the
   button clear of it. Reads 0 whenever the bar is not stuck. */
function initStickyAtcOffset() {
  const bar = document.querySelector('.sticky-add-to-cart__bar');
  const root = document.documentElement;
  if (!bar) {
    root.style.setProperty('--cloverse-sticky-atc-h', '0px');
    return;
  }

  const sync = () => {
    const stuck = bar.getAttribute('data-stuck') === 'true';
    const height = stuck ? Math.round(bar.getBoundingClientRect().height) : 0;
    root.style.setProperty('--cloverse-sticky-atc-h', height + 'px');
  };

  sync();
  new MutationObserver(sync).observe(bar, { attributes: true, attributeFilter: ['data-stuck'] });
  if ('ResizeObserver' in window) new ResizeObserver(sync).observe(bar);
}

/* ---------------------------------------------------------------- Boot */
function boot() {
  initScrollProgress();
  initBackToTop();
  initToasts();
  initStickyAtcOffset();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
